import sidebarStyles from "./sidebar.module.css";
import { Category } from "../../types/types";
import React, { FC, useMemo } from "react";
import useMediaQuery from "../../hooks/useMediaQuery";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAppSelector } from "../../services/hooks";
import avatarPlaceholder from "../../images/avatar_placeholder_purple.svg";
import { mobileWidth } from "../../utils/constants";
import { adminSelector } from "../../services/selectors/user.selectors";
import { menuCategories } from "../../utils/categories";
import { useActions } from "../../services/hooks/useActions";
import SwitchSitesButton from "../switchSitesButton/switchSitesButton";
import { AppImage } from "../appMedia";
import { tWithKeyAttr as t } from "../../i18nHelper";
import { isSuperAdminByRoleId } from "../../services/selectors/admin.selectors";
import packageJson from "../../../package.json";
import { useStayAwake } from "../../hooks/useStayAwake";
import { clearStayAwakeMode } from "../../utils/stayAwakeMode";
import { useEntitlements } from "../../hooks/useEntitlements";

interface ISidebar {
  changeCategory: (
    e:
      | React.MouseEvent<HTMLLIElement, MouseEvent>
      | React.MouseEvent<HTMLAnchorElement, MouseEvent>,
    cat: Category
  ) => void;
  activeCategory: Category;
  toggleMenu: () => void;
  openMenu: boolean;
}

interface SidebarNavItemConfig {
  to: string;
  category: Category;
  labelKey: string;
  iconClass: string;
  testId: string;
  disabled?: boolean;
  hidden?: boolean;
}

interface NavItemProps extends SidebarNavItemConfig {
  activeCategory: Category;
  changeCategory: ISidebar["changeCategory"];
}

const NavItem: FC<NavItemProps> = ({
  to,
  category,
  labelKey,
  iconClass,
  activeCategory,
  changeCategory,
  disabled,
  hidden,
  testId,
}) => {
  if (hidden) return null;
  return (
    <Link
      to={to}
      className={`${sidebarStyles.sidebar__page} ${
        activeCategory === category ? sidebarStyles.sidebar__page_active : ""
      } ${disabled ? "button_disabled" : ""}`}
      data-testid={testId}
      onClick={(e) => {
        if (disabled) {
          e.preventDefault();
          e.stopPropagation();
        } else {
          changeCategory(e, category);
        }
      }}
    >
      <p className={sidebarStyles.sidebar__pageTitle}>{t(labelKey)}</p>
      <div
        className={`${sidebarStyles.sidebar__pageIcon} ${iconClass}`}
      />
    </Link>
  );
};

const TherapistSidebar: FC<ISidebar> = ({
  changeCategory,
  activeCategory,
  toggleMenu,
  openMenu,
}) => {
  const mobile = useMediaQuery(mobileWidth);
  const location = useLocation();
  const navigate = useNavigate();
  const isChat = location.pathname === "/chat";
  const admin = useAppSelector(adminSelector);
  const isCompanyIdValid =
    admin?.companyId && admin?.companyId !== 0 && admin?.companyId !== null;
  const isUserWithFakeCompanyId = Boolean(admin.fakeCompanyId);
  const { hasFeature } = useEntitlements();
  const { logout } = useActions();
  const isSuperAdmin = useAppSelector(isSuperAdminByRoleId);
  const { isActive: isStayAwake, toggle: toggleStayAwake } = useStayAwake();

  const navItems = useMemo((): SidebarNavItemConfig[] => {
    const isAppointmentsDisabled = !isCompanyIdValid;
    const isProtocolsDisabled =
      !isCompanyIdValid;
    const isForumDisabled = !hasFeature("FORUM");
    const isDashboardsDisabled =
      !isCompanyIdValid ||
      isUserWithFakeCompanyId ||
      !hasFeature("CLINICAL_ANALYTICS");

    return [
      {
        to: "/",
        category: Category.Main,
        labelKey: "HOME_SCREEN",
        iconClass: sidebarStyles.sidebar__pageIcon_myDay,
        testId: "home-menu-button",
      },
      {
        to: `/${Category.Users}`,
        category: Category.Users,
        labelKey: "USERS",
        iconClass: sidebarStyles.sidebar__pageIcon_users,
        testId: "users-menu-button",
        disabled: !isCompanyIdValid,
      },
      {
        to: `/${Category.Appointments}`,
        category: Category.Appointments,
        labelKey: menuCategories[Category.Appointments],
        iconClass: sidebarStyles.sidebar__pageIcon_appointments,
        testId: "appointments-menu-button",
        disabled: isAppointmentsDisabled,
      },
      {
        to: `/${Category.Protocols}`,
        category: Category.Protocols,
        labelKey: menuCategories[Category.Protocols],
        iconClass: sidebarStyles.sidebar__pageIcon_protocols,
        testId: "protocols-menu-button",
        disabled: isProtocolsDisabled,
      },
      {
        to: `/${Category.Library}`,
        category: Category.Library,
        labelKey: "PREVENCHER_LIBRARY",
        iconClass: sidebarStyles.sidebar__pageIcon_prevencher,
        testId: "library-menu-button",
      },
      {
        to: `/${Category.VenchiMarketplace}`,
        category: Category.VenchiMarketplace,
        labelKey: menuCategories[Category.VenchiMarketplace],
        iconClass: sidebarStyles.sidebar__pageIcon_venchiMarketplace,
        testId: "venchi-marketplace-menu-button",
        disabled: !isSuperAdmin,
      },
      {
        to: `/${Category.Management}`,
        category: Category.Management,
        labelKey: "MANAGEMENT",
        iconClass: sidebarStyles.sidebar__pageIcon_management,
        testId: "management-menu-button",
        disabled: !isCompanyIdValid,
      },
      {
        to: `/${Category.Forum}`,
        category: Category.Forum,
        labelKey: "FORUM",
        iconClass: sidebarStyles.sidebar__pageIcon_forum,
        testId: "forum-menu-button",
        disabled: isForumDisabled,
      },
      {
        to: `/${Category.Dashboards}`,
        category: Category.Dashboards,
        labelKey: "DASHBOARDS",
        iconClass: sidebarStyles.sidebar__pageIcon_dashboards,
        testId: "dashboards-menu-button",
        disabled: isDashboardsDisabled,
      },
      {
        to: `/${Category.Profile}`,
        category: Category.Profile,
        labelKey: menuCategories[Category.Profile],
        iconClass: sidebarStyles.sidebar__pageIcon_profile,
        testId: "profile-menu-button",
      },
      {
        to: `/${Category.Support}`,
        category: Category.Support,
        labelKey: menuCategories[Category.Support],
        iconClass: sidebarStyles.sidebar__pageIcon_support,
        testId: "support-menu-button",
      },
      {
        to: `/${Category.videoDiagnostics}`,
        category: Category.videoDiagnostics,
        labelKey: menuCategories[Category.videoDiagnostics],
        iconClass: sidebarStyles.sidebar__pageIcon_videoDiagnostics,
        testId: "videoDiagnostics-menu-button",
        hidden: !isSuperAdmin,
      },
      {
        to: `/${Category.TutorialManagement}`,
        category: Category.TutorialManagement,
        labelKey: menuCategories[Category.TutorialManagement],
        iconClass: sidebarStyles.sidebar__pageIcon_prevencher,
        testId: "tutorialManagement-menu-button",
        hidden: !isSuperAdmin,
      },
    ];
  }, [
    isCompanyIdValid,
    isUserWithFakeCompanyId,
    isSuperAdmin,
    hasFeature,
  ]);
  return (
    <section
      className={`${sidebarStyles.sidebar} ${
        isChat && !mobile && sidebarStyles.sidebar_chat
      } ${mobile && openMenu ? sidebarStyles.sidebar_open : ""}`}
      onClick={toggleMenu}
    >
      <div
        className={`${sidebarStyles.sidebar__content_visible} ${
          openMenu && !mobile
            ? ""
            : mobile
            ? sidebarStyles.sidebar_open
            : sidebarStyles.sidebar__content_small
        }`}
        data-testid="sidebar-menu"
      >
        <button
          className={sidebarStyles.sidebar__arrow}
          onClick={toggleMenu}
          data-testid="sidebar-collapse-button"
        />
        <div
          className={sidebarStyles.sidebar__logo}
          data-testid="bpreven-icon"
        />
        <SwitchSitesButton />
        <div className={sidebarStyles.sidebar__welcome} onClick={(e) => e.stopPropagation()}>
          <AppImage
            onClick={(e) => {
              !mobile && e.stopPropagation();
              navigate("/profile", {
                replace: true,
                state: { pathname: "profile" },
              });
            }}
            className={sidebarStyles.sidebar__avatar}
            src={admin.avatar ? admin.avatar : avatarPlaceholder}
            alt={t("USER_IMAGE")}
            data-testid="user-icon"
          />
          <h2 className={sidebarStyles.sidebar__title} data-testid="user-name">
            {admin.firstName ? admin.firstName : t("GUEST")}
          </h2>
        </div>
        <div className={sidebarStyles.scrollable} onClick={(e) => {!mobile && e.stopPropagation()}}>
          <nav className={sidebarStyles.sidebar__pages}>
            {navItems.map((item) => (
              <NavItem
                key={item.category}
                {...item}
                activeCategory={activeCategory}
                changeCategory={changeCategory}
              />
            ))}
          </nav>
          <ul className={sidebarStyles.sidebar__footer}>
          <li
              onClick={(e) => {
                e.preventDefault();
                window.open("https://bpreven.com/privacy-policy/", "_blank");
              }}
            >
              <div className={`${sidebarStyles.sidebar__footerItem}`}>
              <div className={`${sidebarStyles.sidebar__footerIcon} ${sidebarStyles.sidebar__footerIcon_conditions}`} />
                <p className={sidebarStyles.sidebar__footerTitle}>{t("PRIVACY_POLICY")}</p>
              </div>
            </li>
            <li>
              <div
                className={sidebarStyles.sidebar__footerItem}
                data-testid="logout-menu-button"
                onClick={(e) => {
                  e.stopPropagation();
                  clearStayAwakeMode();
                  logout();
                  navigate("/login");
                }}
              >
                <div
                  className={`${sidebarStyles.sidebar__footerIcon} ${sidebarStyles.sidebar__footerIcon_logout}`}
                />
                <p className={sidebarStyles.sidebar__footerTitle}>
                  {t("LOGOUT")}
                </p>
              </div>
            </li>
          </ul>

          {/* Stay Awake toggle */}
          <div className={sidebarStyles.stayAwakeToggle}>
            <label className={sidebarStyles.stayAwakeToggle__label}>
              <span className={sidebarStyles.stayAwakeToggle__text}>
                {t("STAY_AWAKE_TOGGLE") || "Stay Awake"}
              </span>
              <div
                className={`${sidebarStyles.stayAwakeToggle__track} ${isStayAwake ? sidebarStyles.stayAwakeToggle__track_active : ""}`}
                onClick={(e) => { e.stopPropagation(); toggleStayAwake(); }}
                title={isStayAwake
                  ? (t("STAY_AWAKE_TOOLTIP_OFF") || "Disable Stay Awake mode")
                  : (t("STAY_AWAKE_TOOLTIP_ON") || "Enable Stay Awake mode")
                }
              >
                <div className={sidebarStyles.stayAwakeToggle__thumb} />
              </div>
            </label>
          </div>
        </div>
        <p className={sidebarStyles.version} data-testid="sidebar-version">
          {t('VERSION', {version: packageJson.version})}
        </p>
      </div>
    </section>
  );
};

export default TherapistSidebar;
